import {
  CHAIN_ICONS,
  type SupportedTokens,
  TOKEN_ICONS,
} from '@/lib/constants';
import type { CrossChainRoute } from '@/lib/query-options';
import { cn } from '@/lib/utils';
import MingcuteArrowRightLine from '~icons/mingcute/arrow-right-line';
import { ScrollArea } from '../ui/scroll-area';
import type { TransferDirection } from './chain-token-input';

interface TransferRouteListProps {
  routes: CrossChainRoute[];
  dir: TransferDirection;
  activeIndex: number;
  onSelect: (route: CrossChainRoute, token: SupportedTokens) => void;
}

export const TransferRouteList = memo(function TransferRouteList({
  routes,
  dir,
  activeIndex,
  onSelect,
}: TransferRouteListProps) {
  // Currently only USDT is supported
  const supportedToken: SupportedTokens = 'USDT';

  return (
    <ScrollArea className="max-h-[60vh]">
      <div className="space-y-2">
        {routes.map((route, index) => (
          <TransferRouteItem
            key={index}
            dir={dir}
            active={index === activeIndex}
            route={route}
            token={supportedToken}
            onClick={onSelect}
          />
        ))}
      </div>
    </ScrollArea>
  );
});

export interface TransferRouteItemProps {
  dir: TransferDirection;
  route: CrossChainRoute;
  active: boolean;
  token: SupportedTokens;
  onClick: (route: CrossChainRoute, token: SupportedTokens) => void;
}

export const TransferRouteItem = memo(function TransferRouteItem({
  route,
  active,
  token,
  onClick,
}: TransferRouteItemProps) {
  const handleClick = useCallback(() => {
    onClick?.(route, token);
  }, [route, token, onClick]);

  return (
    <button
      type="button"
      className={cn(
        'w-full bg-background ring-1 ring-inset ring-foreground/5 rounded-md flex items-center p-2 hover:bg-background/80 space-x-2 transition-colors',
        active && 'bg-blue-50 ring-blue-300',
      )}
      onClick={handleClick}
    >
      <div className="relative ring-1 ring-foreground/10 rounded-full bg-background shadow-sm">
        <img src={CHAIN_ICONS[route.fromChain]} alt={route.fromChain} className="size-8 overflow-hidden rounded-full" />
        <img
          src={TOKEN_ICONS[token]}
          alt={token}
          className="size-3 absolute bottom-0 right-0 overflow-hidden rounded-full bg-background ring-1 ring-foreground/10"
        />
      </div>

      <div className="flex flex-col items-start flex-1 min-w-12">
        <p className="text-sm font-bold leading-tight">{route.fromChain}</p>
        <p className="text-xs text-foreground/50 leading-tight">{token}</p>
      </div>

      <div
        className={cn(
          'mx-auto -my-2 z-10 ring-1 ring-foreground/10 rounded-full p-1 text-foreground/60',
          active && 'ring-blue-400 bg-blue-100/50 text-blue-500',
        )}
      >
        <MingcuteArrowRightLine className="size-4" />
      </div>

      <div className="flex flex-col items-end flex-1 min-w-12">
        <p className="text-sm font-bold leading-tight">{route.toChain}</p>
        <p className="text-xs text-foreground/50 leading-tight">{token}</p>
      </div>

      <div className="relative ring-1 ring-foreground/10 rounded-full bg-background shadow-sm">
        <img src={CHAIN_ICONS[route.toChain]} alt={route.toChain} className="size-8 overflow-hidden rounded-full" />
        <img
          src={TOKEN_ICONS[token]}
          alt={token}
          className="size-3 absolute bottom-0 right-0 overflow-hidden rounded-full bg-background ring-1 ring-foreground/10"
        />
      </div>
    </button>
  );
});


